import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ListResponse } from '../../../../../../models/list-response';
import { MembershipCategory } from '../../../../../../models/membership';
import { dateStringFromISO8601 } from '../../../../../../utils';
import { AccountService } from '../../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { MembershipService } from '../../../../../../services/membership/membership.service';


@Component({
  selector: 'app-membership-categories',
  templateUrl: './membership-categories.component.html',
  styleUrls: ['./membership-categories.component.css']
})
export class MembershipCategoriesComponent implements OnInit {
  public resultList: ListResponse<MembershipCategory>;

  public categoriesReorderedSubject = new Subject<null>();
  public reorderRequestCount = 0;

  public loadingResults = false;
  public loadingReorder = false;

  public serverError: number = null;
  public errorMessage = '';
  public successMessage = '';

  // Make browser history available to the template.
  public history: History;

  public dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public accountService: AccountService,
    public orgConfig: OrganizationConfigService,
    private activatedRoute: ActivatedRoute,
    private membershipService: MembershipService,
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().is_organization_admin) {
      return;
    }

    // Make browser history available to the template.
    this.history = history;

    this.loadCategories();

    this.categoriesReorderedSubject.pipe(
      // Avoid rapid requests by waiting 1s before applying the order.
      debounceTime(1000)
    ).subscribe(() => {
      this.updateCategoryOrderNumbers();
    });
  }

  public loadCategories() {
    this.loadingResults = true;

    this.membershipService
      .getMembershipCategories(1, 100)
      .then(response => {
        this.resultList = response;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingResults = false;
      });
  }

  /**
   * Whether a non-zero number of results have been loaded.
   */
  public resultsPresent(): boolean {
    return (
      this.resultList &&
      this.resultList.results &&
      (this.resultList.results.length > 0)
    );
  }

  /**
   * Move the given category up or down in the list.
   */
  public shiftCategory(category: MembershipCategory, shiftUp: boolean, toEnd: boolean) {
    const idx = this.resultList.results.indexOf(category);

    if (toEnd) {
      if (shiftUp) {
        this.resultList.results.splice(idx, 1);
        this.resultList.results.unshift(category);
      } else {
        this.resultList.results.splice(idx, 1);
        this.resultList.results.push(category);
      }
    } else {
      if (shiftUp) {
        this.resultList.results[idx] = this.resultList.results[idx - 1];
        this.resultList.results[idx - 1] = category;
      } else {
        this.resultList.results[idx] = this.resultList.results[idx + 1];
        this.resultList.results[idx + 1] = category;
      }
    }

    this.loadingReorder = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.categoriesReorderedSubject.next();
  }

  public updateCategoryOrderNumbers() {
    this.loadingReorder = true;

    this.errorMessage = '';
    this.successMessage = '';

    this.reorderRequestCount++;
    const thisRequestCount = this.reorderRequestCount;

    let orderNum = 1;
    for (const ec of this.resultList.results) {
      ec.order_number = orderNum + 1;
      orderNum += 1;
    }

    this.membershipService
      .setMembershipCategoriesOrderNumbers(this.resultList.results)
      .then(() => {
        this.successMessage = 'Categories reordered successfully.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to reorder these categories.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.reorderRequestCount) {
          this.loadingReorder = false;
        }
      });
  }
}
